import { OVERPASS_URL, OVERPASS_FALLBACK_URL } from '../constants.js';

/**
 * Compute a bounding box around a center point with the given radius in meters.
 */
function bboxFromCenter(lat, lng, radiusMeters) {
  const latDelta = (radiusMeters / 6_371_000) * (180 / Math.PI);
  const lngDelta = latDelta / Math.cos(lat * Math.PI / 180);
  return {
    south: lat - latDelta,
    north: lat + latDelta,
    west:  lng - lngDelta,
    east:  lng + lngDelta,
  };
}

/** Build the Overpass QL query string. */
function buildQuery(south, west, north, east) {
  const b = `${south},${west},${north},${east}`;
  return `[out:json][timeout:60];
(
  way["highway"](${b});
  way["building"](${b});
  relation["building"]["type"="multipolygon"](${b});
  way["landuse"](${b});
  way["natural"](${b});
  way["leisure"](${b});
  way["waterway"~"^(river|stream|canal|drain)$"](${b});
  way["railway"~"^(rail|tram|light_rail|narrow_gauge|subway)$"](${b});
  way["barrier"~"^(wall|fence|hedge|retaining_wall|guard_rail)$"](${b});
  node["natural"="tree"](${b});
  node["amenity"="fountain"](${b});
  node["highway"="traffic_signals"](${b});
);
out body;
>;
out skel qt;`;
}

/** Cache key rounded to ~111m precision. Version prefix forces refresh when query changes. */
function cacheKey(south, west, north, east) {
  const r = (n) => Math.round(n * 1000) / 1000;
  return `osm3d_v2:${r(south)},${r(west)},${r(north)},${r(east)}`;
}

/** Cache validity: 24 hours. */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** Attempt a POST to one Overpass endpoint. */
async function postOverpass(url, query) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${encodeURIComponent(query)}`,
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

/**
 * Fetch OSM data for the area around (lat, lng) within radiusMeters.
 * Results are cached in localStorage for 24 h.
 */
export async function fetchOSMData(lat, lng, radiusMeters) {
  const bbox = bboxFromCenter(lat, lng, radiusMeters);
  const key  = cacheKey(bbox.south, bbox.west, bbox.north, bbox.east);

  // --- Try localStorage cache ---
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp < CACHE_TTL_MS) return data;
      localStorage.removeItem(key);
    }
  } catch {
    // Corrupt cache entry — ignore
  }

  // --- Fetch from Overpass ---
  const query = buildQuery(bbox.south, bbox.west, bbox.north, bbox.east);
  let data;

  try {
    data = await postOverpass(OVERPASS_URL, query);
  } catch {
    // Primary failed — try fallback
    data = await postOverpass(OVERPASS_FALLBACK_URL, query);
  }

  if (!data?.elements) throw new Error('Invalid Overpass response');

  // --- Store in cache ---
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // QuotaExceededError — clear and retry once
    try {
      localStorage.clear();
      localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch { /* ignore — cache is best-effort */ }
  }

  return data;
}
